import React, { useState } from "react";
import { Button, Divider, Image, Input, message, Modal, notification, Upload, Space } from "antd";
import { CameraOutlined, DeleteOutlined, EditOutlined, FileOutlined, BugOutlined, BulbOutlined } from "@ant-design/icons";
import html2canvas from "html2canvas";
import ImageEditModal from "./ImageDraw";
import { isSustrax } from "../../../helper/plans";

const SCREENSHOTTIME = 0.3;

const UserHelpModal = ({
  modalVisible,
  setModalVisible,
  type = "feature", // "feature" or "bug"
  onSubmit,
  showFileUpload = false,
}) => {
  const [data, setData] = useState({ 
    tag: type === "feature" ? "Feature Request" : "Bug" 
  });
  const [errs, setErrs] = useState({ text: null });
  const [drawModal, setDrawModal] = useState(false);
  const [loader, setLoader] = useState(false);

  const isFeatureRequest = type === "feature";
  const title = isFeatureRequest ? "Request a Feature" : "Report a Bug";
  const icon = isFeatureRequest ? <BulbOutlined /> : <BugOutlined />;
  const placeholder = isFeatureRequest 
    ? "Describe the feature you would like to request."
    : "Write detailed description about the bug.";

  const closeModal = () => {
    setModalVisible(false);
    setData({ tag: type === "feature" ? "Feature Request" : "Bug" });
    setErrs({});
  };

  const takeFullPageScreenshot = () => {
    setModalVisible(false);
    message.loading({
      type: "loading",
      content: "Taking Screenshot",
      duration: SCREENSHOTTIME,
    });

    setTimeout(() => {
      const pageElement = document.documentElement;
      const options = {
        scrollX: 0,
        scrollY: -window.scrollY,
        scale: 1,
      };

      html2canvas(pageElement, options).then((canvas) => {
        const url = canvas.toDataURL();
        setModalVisible(true);
        setData({ ...data, screenshotUrl: url });
      });
    }, SCREENSHOTTIME * 1000);
  };

  const handleSendUpdate = async () => {
    if (!data.text || data.text === "") {
      return setErrs({ ...errs, text: "Add some text" });
    }
    setLoader(true);
    try {
      await onSubmit(data);
      closeModal();
      if (isFeatureRequest) {
        notification.success({
          message: "Feature Request Submitted",
          description: "We will review your feature request and get back to you soon.",
          placement: "top",
        });
      } else {
        message.success("Bug has been reported!");
      }
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoader(false);
    }
  };

  const handleFileUpload = (file) => {
    const isValidSize = file.size / 1024 / 1024 < 10; // 10MB limit
    if (!isValidSize) {
      message.error('File must be smaller than 10MB!');
      return false;
    }
    setData({ ...data, file: file });
    message.success(`${file.name} uploaded successfully`);
    return false; // Prevent default upload behavior
  };

  const removeFile = () => {
    setData({ ...data, file: null });
  };

  const removeScreenshot = () => {
    setData({ ...data, screenshotUrl: "" });
  };

  return (
    <>
      <Modal 
        title={
          <Space>
            {icon}
            {title}
          </Space>
        } 
        open={modalVisible} 
        onCancel={closeModal} 
        okText="Send" 
        onOk={handleSendUpdate} 
        confirmLoading={loader}
        width={500}
      >
        {/* Description Input */}
        <div style={{ marginBottom: '16px' }}>
          <Input.TextArea
            value={data.text}
            onChange={(e) => {
              setData({ ...data, text: e.target.value });
              if (errs.text) setErrs({ ...errs, text: null });
            }}
            placeholder={placeholder}
            status={errs.text ? "error" : ""}
            rows={4}
            style={{ resize: 'none' }}
          />
          {errs.text && (
            <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
              {errs.text}
            </div>
          )}
        </div>

        {/* Screenshot Section */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
            Screenshot (optional)
          </div>
          {!data.screenshotUrl ? (
            <Button 
              icon={<CameraOutlined />} 
              onClick={takeFullPageScreenshot}
              size="small"
              style={{ width: '100%' }}
            >
              Add Screenshot
            </Button>
          ) : (
            <div>
              <div style={{ marginBottom: '8px' }}>
                <Image 
                  width="100%" 
                  src={data.screenshotUrl} 
                  alt="Screenshot"
                  style={{ 
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9',
                    maxHeight: '150px',
                    objectFit: 'cover'
                  }}
                />
              </div>
              <Space size="small">
                <Button 
                  icon={<EditOutlined />} 
                  onClick={() => setDrawModal(true)}
                  size="small"
                  type="text"
                >
                  Edit
                </Button>
                <Button 
                  icon={<DeleteOutlined />}
                  onClick={removeScreenshot}
                  size="small"
                  type="text"
                  danger
                >
                  Remove
                </Button>
              </Space>
            </div>
          )}
        </div>

        {/* File Upload Section */}
        {(showFileUpload || (isFeatureRequest && !isSustrax("free"))) && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              Supporting File (optional)
            </div>
            {!data.file ? (
              <Upload
                beforeUpload={handleFileUpload}
                showUploadList={false}
                accept="*"
              >
                <Button 
                  icon={<FileOutlined />}
                  size="small"
                  style={{ width: '100%' }}
                >
                  Upload File
                </Button>
              </Upload>
            ) : (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '6px 8px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}>
                <Space size="small">
                  <FileOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
                  <span style={{ fontSize: '13px' }}>{data.file.name}</span>
                  <span style={{ 
                    fontSize: '11px', 
                    color: '#8c8c8c' 
                  }}>
                    ({(data.file.size / 1024 / 1024).toFixed(1)} MB)
                  </span>
                </Space>
                <Button 
                  icon={<DeleteOutlined />}
                  onClick={removeFile}
                  danger
                  size="small"
                  type="text"
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      <ImageEditModal 
        visible={drawModal} 
        setVisible={setDrawModal} 
        imageUrl={data.screenshotUrl} 
        setDataUrl={(screenshotUrl) => setData({ ...data, screenshotUrl })} 
      />
    </>
  );
};

export default UserHelpModal; 